import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface PaginationProps {
  page: number;
  totalPages?: number;
  total?: number;
  limit: number;
  hasNext?: boolean;
  hasPrev?: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  itemLabel?: string;
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];

  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');

  pages.push(total);
  return pages;
}

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  hasNext,
  hasPrev,
  onPageChange,
  onLimitChange,
  itemLabel = 'orders',
}: PaginationProps) {
  // Classic pagination (when total and totalPages are known)
  if (total !== undefined && totalPages !== undefined) {
    if (totalPages <= 1 && total <= limit) return null;

    const from = Math.min((page - 1) * limit + 1, total);
    const to = Math.min(page * limit, total);
    const pages = getPageNumbers(page, totalPages);

    return (
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <p className="text-base font-normal text-lightGray">
          Showing <span className="font-semibold text-black">{from}–{to}</span> of <span className="font-semibold text-black">{total}</span> {itemLabel}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-normal text-lightGray whitespace-nowrap">Per page</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex h-8 items-center gap-1.5 rounded-lg border border-smoke bg-neutral-white px-3 text-base font-normal text-black hover:bg-smoke1">
                  {limit}
                  <ChevronDown size={13} className="text-silver" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => { onLimitChange(s); onPageChange(1); }} className={s === limit ? 'bg-primary-s50 text-primary-s500' : ''}>
                    {s}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="flex h-8 w-8 items-center justify-center rounded-lg border border-smoke bg-neutral-white text-lightGray transition-colors hover:bg-smoke1 disabled:cursor-not-allowed disabled:opacity-40">
              <ChevronLeft size={15} />
            </button>
            {pages.map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="flex h-8 w-8 items-center justify-center text-base font-normal text-lightGray">…</span>
              ) : (
                <button key={p} type="button" onClick={() => onPageChange(p as number)} className={`flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-base font-normal transition-colors ${p === page ? 'bg-primary-s500 text-neutral-white' : 'border border-smoke bg-neutral-white text-lightGray hover:bg-smoke1'}`}>
                  {p}
                </button>
              )
            )}
            <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="flex h-8 w-8 items-center justify-center rounded-lg border border-smoke bg-neutral-white text-lightGray transition-colors hover:bg-smoke1 disabled:cursor-not-allowed disabled:opacity-40">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Next/Prev pagination (when total is unknown)
  if (!hasNext && page === 1) return null; // No pages to show

  const from = (page - 1) * limit + 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
      <p className="text-base font-normal text-lightGray">
        Page <span className="font-semibold text-black">{page}</span>
      </p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-base font-normal text-lightGray whitespace-nowrap">Per page</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex h-8 items-center gap-1.5 rounded-lg border border-smoke bg-neutral-white px-3 text-base font-normal text-black hover:bg-smoke1">
                {limit}
                <ChevronDown size={13} className="text-silver" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PAGE_SIZE_OPTIONS.map((s) => (
                <DropdownMenuItem key={s} onClick={() => { onLimitChange(s); onPageChange(1); }} className={s === limit ? 'bg-primary-s50 text-primary-s500' : ''}>
                  {s}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1 || hasPrev === false} className="flex h-8 w-8 items-center justify-center rounded-lg border border-smoke bg-neutral-white text-lightGray transition-colors hover:bg-smoke1 disabled:cursor-not-allowed disabled:opacity-40">
            <ChevronLeft size={15} />
          </button>
          <span className="flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-base font-normal border border-smoke bg-neutral-white text-black">
            {page}
          </span>
          <button type="button" onClick={() => onPageChange(page + 1)} disabled={hasNext === false} className="flex h-8 w-8 items-center justify-center rounded-lg border border-smoke bg-neutral-white text-lightGray transition-colors hover:bg-smoke1 disabled:cursor-not-allowed disabled:opacity-40">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
